import {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {PlacesFilterObject} from '../services/api/types/types';

export interface SearchFilterMap {
    reason: Array<string>;
    other: Array<string>;
    place: Array<string>;
    caregiver: Array<string>;
    clinic: Array<string>;
    location: Array<string>;
}

export interface PlaceFilterMap {
    [id: string]: PlacesFilterObject;
}

const preservedSearchParamKeys = ['from', 'to', 'timeOfDay'];

function filtersToQuery(filters: SearchFilterMap) {
    const reasons = filters.reason.length > 0 ? [`reason=${filters.reason}`] : [];
    const other = filters.other.length > 0 ? [`other=${filters.other}`] : [];
    const place = filters.place.length > 0 ? [`place=${filters.place}`] : [];
    const caregiver = filters.caregiver.length > 0 ? [`caregiver=${filters.caregiver}`] : [];
    const clinic = filters.clinic.length > 0 ? [`clinic=${filters.clinic}`] : [];
    const location = filters.location.length > 0 ? [`location=${filters.location}`] : [];

    return [...reasons, ...other, ...place, ...caregiver, ...clinic, ...location].join('&');
}

export function useSearchFilters() {
    const [urlSearchParams] = useSearchParams();
    const [searchParams, setSearchParams] = useState<SearchFilterMap>({
        reason: urlSearchParams.get('reason')?.split(',') || [],
        other: urlSearchParams.get('other')?.split(',') || [],
        place: urlSearchParams.get('place')?.split(',') || [],
        caregiver: urlSearchParams.get('caregiver')?.split(',') || [],
        clinic: urlSearchParams.get('clinic')?.split(',') || [],
        location: urlSearchParams.get('location')?.split(',') || []
    });
    const [placeFilters, setPlaceFilters] = useState<PlaceFilterMap>({});

    const navigate = useNavigate();

    useEffect(() => {
        setSearchParams({
            reason: urlSearchParams.get('reason')?.split(',') || [],
            other: urlSearchParams.get('other')?.split(',') || [],
            place: urlSearchParams.get('place')?.split(',') || [],
            caregiver: urlSearchParams.get('caregiver')?.split(',') || [],
            clinic: urlSearchParams.get('clinic')?.split(',') || [],
            location: urlSearchParams.get('location')?.split(',') || []
        });
    }, [urlSearchParams]);

    const setParamsAndLocation = (newSearchParams: SearchFilterMap, preserveDateTimeParams = true) => {
        updateBrowserLocation(newSearchParams, preserveDateTimeParams);
    };

    const updateBrowserLocation = (filters: SearchFilterMap, preserveDateTimeParams = true) => {
        const nextUrlSearchParams = new URLSearchParams(filtersToQuery(filters));

        if (preserveDateTimeParams) {
            preservedSearchParamKeys.forEach((key) => {
                const value = urlSearchParams.get(key);
                if (value) {
                    nextUrlSearchParams.set(key, value);
                }
            });
        }

        const query = nextUrlSearchParams.toString();

        if (query.length > 0) {
            return navigate(`/hitta?${query}`);
        } else {
            return navigate('/');
        }
    };

    const addFilter = (type: keyof SearchFilterMap, id: string) => {
        // Avoid adding duplicates of the same filter
        const filterArr = searchParams[type].filter((filterId) => filterId !== id);
        filterArr.push(id);

        const newSearchParams = {
            ...searchParams,
            [type]: filterArr
        };
        setParamsAndLocation(newSearchParams);
    };

    const removeFilter = (type: keyof SearchFilterMap, id: string) => {
        const filterArr = searchParams[type].filter((filterId) => filterId !== id);

        const newSearchParams = {
            ...searchParams,
            [type]: filterArr
        };
        setParamsAndLocation(newSearchParams);
    };

    const setFilter = (type: keyof SearchFilterMap, ids: Array<string>) => {
        const newSearchParams = {
            ...searchParams,
            [type]: ids
        };
        setParamsAndLocation(newSearchParams);
    };

    return {
        searchParams,
        placeFilters,
        setPlaceFilters,
        searchParamsCount() {
            const {reason, other, place, caregiver, clinic} = searchParams;
            const hasDateRange = urlSearchParams.has('from') && urlSearchParams.has('to');
            const timeOfDay = urlSearchParams.get('timeOfDay');
            const hasTimeOfDayFilter = Boolean(timeOfDay && timeOfDay !== 'ALLDAY');

            return (
                reason.length +
                other.length +
                place.length +
                caregiver.length +
                clinic.length +
                Number(hasDateRange) +
                Number(hasTimeOfDayFilter)
            );
        },
        clearParams() {
            const clearedSearchParams = {
                reason: [],
                tags: [],
                other: [],
                place: [],
                caregiver: [],
                clinic: [],
                location: []
            };
            setParamsAndLocation(clearedSearchParams, false);
        },
        clearParamsByType(type: keyof SearchFilterMap) {
            const newSearchParams = {...searchParams};
            newSearchParams[type] = [];

            setParamsAndLocation(newSearchParams);
        },
        addFilter,
        removeFilter,
        setFilter,
        addPlaceFilter(id: string, {name, category, boundingBox}: PlacesFilterObject) {
            const newFilters = {...placeFilters};

            newFilters[id] = {
                id,
                name,
                boundingBox,
                category
            };

            setPlaceFilters(newFilters);
            addFilter('place', id);
        },
        removePlaceFilter(id: string) {
            const newFilters = {...placeFilters};

            delete newFilters[id];

            setPlaceFilters(newFilters);
            removeFilter('place', id);
        },
        setOthers(other: Array<string>) {
            const newSearchParams = {
                ...searchParams,
                other: other
            };
            setParamsAndLocation(newSearchParams);
        },
        setBoundingBox(places: Array<string>) {
            const newSearchParams = {
                ...searchParams,
                bb: places
            };
            setParamsAndLocation(newSearchParams);
        }
    };
}
